export type QuestionStage = 1 | 2 | 3;
export type QuestionType = "choice" | "blocks";
export type SkillTag =
  | "부사 뉘앙스"
  | "순서 연결"
  | "의도와 목적"
  | "문장 마무리";

export interface BlockToken {
  id: string;
  text: string;
}

export interface Question {
  id: string;
  stage: QuestionStage;
  type: QuestionType;
  prompt: string;
  context?: string;
  options?: string[];
  blocks?: BlockToken[];
  answer: string | string[];
  correctSentence: string;
  explanationKo: string;
  explanationEn: string;
  tag: SkillTag;
}

const blocks = (id: string, values: string[]): BlockToken[] =>
  values.map((text, index) => ({ id: `${id}-${index}`, text }));

export const questions: Question[] = [
  {
    id: "n1",
    stage: 1,
    type: "choice",
    prompt: "이번 학기 교환학생의 ___은 기숙사에 살아요.",
    options: ["대부분", "아마"],
    answer: "대부분",
    correctSentence: "이번 학기 교환학생의 대부분은 기숙사에 살아요.",
    explanationKo: "전체 중 많은 비율을 말할 때는 ‘대부분’을 써요.",
    explanationEn: "Use ‘대부분’ for the majority of a group.",
    tag: "부사 뉘앙스",
  },
  {
    id: "n2",
    stage: 1,
    type: "choice",
    prompt: "회의 준비는 ___ 끝났으니까 곧 시작할게요.",
    options: ["거의", "대략"],
    answer: "거의",
    correctSentence: "회의 준비는 거의 끝났으니까 곧 시작할게요.",
    explanationKo: "완료에 가까운 상태는 ‘거의’, 어림잡은 수치는 ‘대략’이라고 해요.",
    explanationEn: "‘거의’ means almost; ‘대략’ means approximately.",
    tag: "부사 뉘앙스",
  },
  {
    id: "n3",
    stage: 1,
    type: "choice",
    prompt: "하늘이 많이 흐려서 ___ 비가 올 것 같아요.",
    options: ["아마", "대부분"],
    answer: "아마",
    correctSentence: "하늘이 많이 흐려서 아마 비가 올 것 같아요.",
    explanationKo: "확실하지 않은 일을 추측할 때 ‘아마’를 써요.",
    explanationEn: "Use ‘아마’ when making an uncertain prediction.",
    tag: "부사 뉘앙스",
  },
  {
    id: "n4",
    stage: 1,
    type: "choice",
    prompt: "저는 퇴근한 뒤에 ___ 집에서 저녁을 먹어요.",
    options: ["보통", "대략"],
    answer: "보통",
    correctSentence: "저는 퇴근한 뒤에 보통 집에서 저녁을 먹어요.",
    explanationKo: "반복되는 습관이나 평소 행동에는 ‘보통’이 자연스러워요.",
    explanationEn: "Use ‘보통’ for a usual habit or routine.",
    tag: "부사 뉘앙스",
  },
  {
    id: "n5",
    stage: 1,
    type: "choice",
    prompt: "행사에는 ___ 50명 정도가 참석할 예정이에요.",
    options: ["약", "아마"],
    answer: "약",
    correctSentence: "행사에는 약 50명 정도가 참석할 예정이에요.",
    explanationKo: "숫자를 어림잡아 말할 때는 숫자 앞에 ‘약’을 붙일 수 있어요.",
    explanationEn: "Put ‘약’ before a number to mean approximately.",
    tag: "부사 뉘앙스",
  },
  {
    id: "n6",
    stage: 1,
    type: "choice",
    prompt: "보고서를 ___ 다 썼어요. 마지막 부분만 확인하면 돼요.",
    options: ["거의", "보통"],
    answer: "거의",
    correctSentence: "보고서를 거의 다 썼어요. 마지막 부분만 확인하면 돼요.",
    explanationKo: "끝에 매우 가까운 상태이므로 ‘거의 다’가 알맞아요.",
    explanationEn: "‘거의 다’ means nearly all or almost finished.",
    tag: "부사 뉘앙스",
  },
  {
    id: "n7",
    stage: 1,
    type: "choice",
    prompt: "회의는 ___ 한 시간 정도 걸릴 거예요.",
    options: ["대략", "대부분"],
    answer: "대략",
    correctSentence: "회의는 대략 한 시간 정도 걸릴 거예요.",
    explanationKo: "시간을 어림잡아 말하므로 ‘대략’이 자연스러워요.",
    explanationEn: "Use ‘대략’ for an approximate amount of time.",
    tag: "부사 뉘앙스",
  },
  {
    id: "n8",
    stage: 1,
    type: "choice",
    prompt: "설문에 참여한 사람들의 ___이 새 기능에 만족했어요.",
    options: ["대부분", "아마"],
    answer: "대부분",
    correctSentence: "설문에 참여한 사람들의 대부분이 새 기능에 만족했어요.",
    explanationKo: "참여자 중 많은 수를 가리키므로 ‘대부분’이 알맞아요.",
    explanationEn: "‘대부분’ refers to most of the participants.",
    tag: "부사 뉘앙스",
  },
  {
    id: "n9",
    stage: 1,
    type: "choice",
    prompt: "길이 많이 막혀서 ___ 약속에 늦을 것 같아요.",
    options: ["아마", "보통"],
    answer: "아마",
    correctSentence: "길이 많이 막혀서 아마 약속에 늦을 것 같아요.",
    explanationKo: "현재 상황을 근거로 결과를 추측하고 있어요.",
    explanationEn: "‘아마’ fits a prediction based on the current situation.",
    tag: "부사 뉘앙스",
  },
  {
    id: "n10",
    stage: 1,
    type: "choice",
    prompt: "저는 주말 아침에는 ___ 늦게 일어나는 편이에요.",
    options: ["보통", "거의"],
    answer: "보통",
    correctSentence: "저는 주말 아침에는 보통 늦게 일어나는 편이에요.",
    explanationKo: "평소의 경향을 말하므로 ‘보통’이 자연스러워요.",
    explanationEn: "Use ‘보통’ to describe what you usually do.",
    tag: "부사 뉘앙스",
  },
  {
    id: "n11",
    stage: 1,
    type: "choice",
    prompt: "새 시스템 사용법을 ___ 다 익혔어요. 어려운 기능 두 개만 남았어요.",
    options: ["거의", "약"],
    answer: "거의",
    correctSentence: "새 시스템 사용법을 거의 다 익혔어요. 어려운 기능 두 개만 남았어요.",
    explanationKo: "전체 학습이 끝나기 직전의 상태이므로 ‘거의 다’가 자연스러워요.",
    explanationEn: "Use ‘거의 다’ because the learning is nearly complete, with only a small part remaining.",
    tag: "부사 뉘앙스",
  },
  {
    id: "n12",
    stage: 1,
    type: "choice",
    prompt: "신입 직원 교육은 ___ 두 시간 정도 진행될 예정이에요.",
    options: ["대략", "보통"],
    answer: "대략",
    correctSentence: "신입 직원 교육은 대략 두 시간 정도 진행될 예정이에요.",
    explanationKo: "정확하지 않은 예상 시간을 말하므로 ‘대략’이 알맞아요.",
    explanationEn: "Use ‘대략’ to give an estimated duration rather than describe a usual habit.",
    tag: "부사 뉘앙스",
  },
  {
    id: "n13",
    stage: 1,
    type: "choice",
    prompt: "우리 팀 직원들의 ___이 재택근무를 선호해요.",
    options: ["대부분", "아마"],
    answer: "대부분",
    correctSentence: "우리 팀 직원들의 대부분이 재택근무를 선호해요.",
    explanationKo: "팀원 중 큰 비율을 가리키므로 ‘대부분’을 사용해요.",
    explanationEn: "‘대부분’ identifies most members of the team; ‘아마’ would express uncertainty instead.",
    tag: "부사 뉘앙스",
  },
  {
    id: "n14",
    stage: 1,
    type: "choice",
    prompt: "저는 점심시간에는 ___ 동료들과 근처 식당에 가요.",
    options: ["보통", "거의"],
    answer: "보통",
    correctSentence: "저는 점심시간에는 보통 동료들과 근처 식당에 가요.",
    explanationKo: "반복되는 점심 습관을 말하므로 ‘보통’이 자연스러워요.",
    explanationEn: "Use ‘보통’ for a regular lunchtime routine, not for something that is almost complete.",
    tag: "부사 뉘앙스",
  },
  {
    id: "n15",
    stage: 1,
    type: "choice",
    prompt: "발표 자료는 ___ 20페이지 정도로 만들 계획이에요.",
    options: ["약", "거의"],
    answer: "약",
    correctSentence: "발표 자료는 약 20페이지 정도로 만들 계획이에요.",
    explanationKo: "예상 페이지 수처럼 숫자를 어림잡을 때는 숫자 앞에 ‘약’을 써요.",
    explanationEn: "Place ‘약’ before the number to show that 20 pages is an approximate amount.",
    tag: "부사 뉘앙스",
  },
  {
    id: "n16",
    stage: 1,
    type: "choice",
    prompt: "아직 답장이 없는 걸 보니 ___ 회의 중인 것 같아요.",
    options: ["아마", "대부분"],
    answer: "아마",
    correctSentence: "아직 답장이 없는 걸 보니 아마 회의 중인 것 같아요.",
    explanationKo: "답장이 없는 상황을 근거로 가능성을 추측하므로 ‘아마’를 써요.",
    explanationEn: "Use ‘아마’ because the speaker is making an uncertain guess from the lack of a reply.",
    tag: "부사 뉘앙스",
  },
  {
    id: "n17",
    stage: 1,
    type: "choice",
    prompt: "오늘 작업은 ___ 끝나서 마지막 확인만 하면 돼요.",
    options: ["거의", "대략"],
    answer: "거의",
    correctSentence: "오늘 작업은 거의 끝나서 마지막 확인만 하면 돼요.",
    explanationKo: "작업이 완료에 가까운 상태이므로 ‘거의’가 알맞아요.",
    explanationEn: "‘거의’ describes work that is close to finished; ‘대략’ would estimate a measurable value.",
    tag: "부사 뉘앙스",
  },
  {
    id: "c1",
    stage: 2,
    type: "blocks",
    prompt: "두 행동을 자연스러운 순서로 이어 보세요.",
    context: "영화를 보다 → 밥을 먹다",
    blocks: blocks("c1", ["밥을", "보고 나서", "먹으려고 해요", "영화를"]),
    answer: ["c1-3", "c1-1", "c1-0", "c1-2"],
    correctSentence: "영화를 보고 나서 밥을 먹으려고 해요.",
    explanationKo: "먼저 끝난 행동 뒤에 ‘-고 나서’를 붙여 다음 행동과 연결해요.",
    explanationEn: "Use ‘-고 나서’ after the action that happens first.",
    tag: "순서 연결",
  },
  {
    id: "c2",
    stage: 2,
    type: "blocks",
    prompt: "업무 순서에 맞게 문장을 완성하세요.",
    context: "보고서를 확인하다 → 팀장님께 보내다",
    blocks: blocks("c2", ["팀장님께", "보고서를 확인한 뒤에", "보낼게요"]),
    answer: ["c2-1", "c2-0", "c2-2"],
    correctSentence: "보고서를 확인한 뒤에 팀장님께 보낼게요.",
    explanationKo: "‘-(으)ㄴ 뒤에’는 앞 행동이 끝난 후의 일을 나타내요.",
    explanationEn: "‘-(으)ㄴ 뒤에’ means after completing the first action.",
    tag: "순서 연결",
  },
  {
    id: "c3",
    stage: 2,
    type: "blocks",
    prompt: "약속을 자연스럽게 이어 보세요.",
    context: "퇴근하다 → 친구를 만나다",
    blocks: blocks("c3", ["만나기로 했어요", "퇴근하고 나서", "친구를"]),
    answer: ["c3-1", "c3-2", "c3-0"],
    correctSentence: "퇴근하고 나서 친구를 만나기로 했어요.",
    explanationKo: "앞 행동의 완료와 이미 정한 약속을 자연스럽게 이은 문장이에요.",
    explanationEn: "The sentence connects a completed action with an arranged plan.",
    tag: "순서 연결",
  },
  {
    id: "c4",
    stage: 2,
    type: "blocks",
    prompt: "몸이 아픈 상황을 이어 보세요.",
    context: "약을 먹다 → 쉬다",
    blocks: blocks("c4", ["조금", "약을 먹은 다음에", "쉬려고 해요"]),
    answer: ["c4-1", "c4-0", "c4-2"],
    correctSentence: "약을 먹은 다음에 조금 쉬려고 해요.",
    explanationKo: "‘-(으)ㄴ 다음에’로 순서를, ‘-(으)려고 해요’로 계획을 나타내요.",
    explanationEn: "The first ending shows sequence; the second shows a plan.",
    tag: "의도와 목적",
  },
  {
    id: "c5",
    stage: 2,
    type: "blocks",
    prompt: "업무 상황을 순서대로 조립하세요.",
    context: "상황을 확인하다 → 팀원들에게 공유하다",
    blocks: blocks("c5", ["상황을 확인하고 나서", "공유했어요", "팀원들에게"]),
    answer: ["c5-0", "c5-2", "c5-1"],
    correctSentence: "상황을 확인하고 나서 팀원들에게 공유했어요.",
    explanationKo: "확인한 일이 먼저, 공유한 일이 나중에 일어났어요.",
    explanationEn: "Checking happened first, and sharing happened afterward.",
    tag: "순서 연결",
  },
  {
    id: "c6",
    stage: 2,
    type: "blocks",
    prompt: "계약 과정을 자연스럽게 이어 보세요.",
    context: "계약서를 읽어 보다 → 서명하다",
    blocks: blocks("c6", ["서명하려고 합니다", "읽어 본 뒤에", "계약서를"]),
    answer: ["c6-2", "c6-1", "c6-0"],
    correctSentence: "계약서를 읽어 본 뒤에 서명하려고 합니다.",
    explanationKo: "검토가 먼저 끝난 후 서명할 의도를 표현한 문장이에요.",
    explanationEn: "The sentence expresses an intention after reviewing the contract.",
    tag: "의도와 목적",
  },
  {
    id: "c7",
    stage: 2,
    type: "blocks",
    prompt: "점심시간의 행동을 이어 보세요.",
    context: "점심을 먹다 → 산책하다",
    blocks: blocks("c7", ["회사 근처를", "산책했어요", "점심을 먹고 나서"]),
    answer: ["c7-2", "c7-0", "c7-1"],
    correctSentence: "점심을 먹고 나서 회사 근처를 산책했어요.",
    explanationKo: "두 행동이 실제로 일어난 순서를 자연스럽게 보여 줘요.",
    explanationEn: "This naturally shows the order of two completed actions.",
    tag: "순서 연결",
  },
  {
    id: "c8",
    stage: 2,
    type: "blocks",
    prompt: "발표 순서에 맞게 조립하세요.",
    context: "발표가 끝나다 → 질문을 받다",
    blocks: blocks("c8", ["질문을 받을게요", "발표가 끝난 다음에"]),
    answer: ["c8-1", "c8-0"],
    correctSentence: "발표가 끝난 다음에 질문을 받을게요.",
    explanationKo: "앞 일이 끝난 후 할 행동을 ‘-(으)ㄹ게요’로 마무리했어요.",
    explanationEn: "‘-(으)ㄹ게요’ closes the sentence with the speaker's intention.",
    tag: "문장 마무리",
  },
  {
    id: "c9",
    stage: 2,
    type: "blocks",
    prompt: "구매 전 행동을 순서대로 이어 보세요.",
    context: "가격과 후기를 비교하다 → 결정하다",
    blocks: blocks("c9", ["결정하려고 해요", "가격과 후기를", "비교해 본 뒤에"]),
    answer: ["c9-1", "c9-2", "c9-0"],
    correctSentence: "가격과 후기를 비교해 본 뒤에 결정하려고 해요.",
    explanationKo: "충분히 비교한 다음 결정하겠다는 의도를 나타내요.",
    explanationEn: "It expresses the intention to decide after comparing options.",
    tag: "의도와 목적",
  },
  {
    id: "c10",
    stage: 2,
    type: "blocks",
    prompt: "귀가 후 습관을 자연스럽게 조립하세요.",
    context: "집에 도착하다 → 손을 씻다",
    blocks: blocks("c10", ["먼저 손을 씻어요", "집에 도착하면"]),
    answer: ["c10-1", "c10-0"],
    correctSentence: "집에 도착하면 먼저 손을 씻어요.",
    explanationKo: "반복되는 상황과 습관은 ‘-(으)면’으로 자연스럽게 연결할 수 있어요.",
    explanationEn: "Use ‘-(으)면’ for a repeated situation followed by a habit.",
    tag: "문장 마무리",
  },
  {
    id: "c11",
    stage: 2,
    type: "blocks",
    prompt: "예약 순서에 맞게 문장을 조립하세요.",
    context: "일정을 확인하다 → 회의실을 예약하다",
    blocks: blocks("c11", ["회의실을", "일정을 확인한 뒤에", "예약했어요"]),
    answer: ["c11-1", "c11-0", "c11-2"],
    correctSentence: "일정을 확인한 뒤에 회의실을 예약했어요.",
    explanationKo: "일정 확인이 먼저 끝난 후 예약이 이어지므로 ‘-(으)ㄴ 뒤에’를 사용해요.",
    explanationEn: "Use ‘-(으)ㄴ 뒤에’ because checking the schedule is completed before booking the room.",
    tag: "순서 연결",
  },
  {
    id: "c12",
    stage: 2,
    type: "blocks",
    prompt: "출근 후 행동을 자연스럽게 이어 보세요.",
    context: "컴퓨터를 켜다 → 이메일을 확인하다",
    blocks: blocks("c12", ["이메일을 확인해요", "컴퓨터를 켜고 나서"]),
    answer: ["c12-1", "c12-0"],
    correctSentence: "컴퓨터를 켜고 나서 이메일을 확인해요.",
    explanationKo: "컴퓨터를 켜는 행동이 끝난 뒤 이메일 확인이 이어져요.",
    explanationEn: "‘-고 나서’ places checking email after the computer has been turned on.",
    tag: "순서 연결",
  },
  {
    id: "c13",
    stage: 2,
    type: "blocks",
    prompt: "수업 후 계획을 순서대로 조립하세요.",
    context: "수업이 끝나다 → 도서관에 가다",
    blocks: blocks("c13", ["도서관에 갈 거예요", "수업이 끝난 다음에"]),
    answer: ["c13-1", "c13-0"],
    correctSentence: "수업이 끝난 다음에 도서관에 갈 거예요.",
    explanationKo: "앞 사건의 완료 뒤에 이어질 계획을 ‘-(으)ㄴ 다음에’로 연결해요.",
    explanationEn: "Use ‘-(으)ㄴ 다음에’ to connect the completed class with the plan that follows.",
    tag: "문장 마무리",
  },
  {
    id: "c14",
    stage: 2,
    type: "blocks",
    prompt: "행동의 목적이 드러나도록 조립하세요.",
    context: "비를 맞지 않다 → 우산을 챙기다",
    blocks: blocks("c14", ["우산을 챙겼어요", "비를 맞지 않으려고"]),
    answer: ["c14-1", "c14-0"],
    correctSentence: "비를 맞지 않으려고 우산을 챙겼어요.",
    explanationKo: "우산을 챙긴 목적이 비를 피하는 것이므로 ‘-지 않으려고’를 사용해요.",
    explanationEn: "‘-지 않으려고’ expresses that the umbrella was taken in order to avoid getting wet.",
    tag: "의도와 목적",
  },
  {
    id: "c15",
    stage: 2,
    type: "blocks",
    prompt: "주문 과정을 자연스럽게 이어 보세요.",
    context: "메뉴를 살펴보다 → 음식을 주문하다",
    blocks: blocks("c15", ["음식을 주문했어요", "메뉴를 살펴보고 나서"]),
    answer: ["c15-1", "c15-0"],
    correctSentence: "메뉴를 살펴보고 나서 음식을 주문했어요.",
    explanationKo: "메뉴 확인이 끝난 뒤 주문한 실제 순서를 ‘-고 나서’로 보여 줘요.",
    explanationEn: "‘-고 나서’ clearly shows that reviewing the menu happened before ordering.",
    tag: "순서 연결",
  },
  {
    id: "c16",
    stage: 2,
    type: "blocks",
    prompt: "반복되는 업무 습관을 조립하세요.",
    context: "월요일이 되다 → 이번 주 일정을 정리하다",
    blocks: blocks("c16", ["이번 주 일정을 정리해요", "월요일이 되면"]),
    answer: ["c16-1", "c16-0"],
    correctSentence: "월요일이 되면 이번 주 일정을 정리해요.",
    explanationKo: "매주 반복되는 조건과 습관은 ‘-(으)면’으로 연결해요.",
    explanationEn: "Use ‘-(으)면’ because organizing the schedule regularly follows the same weekly condition.",
    tag: "문장 마무리",
  },
  {
    id: "c17",
    stage: 2,
    type: "blocks",
    prompt: "상의 후 결정을 자연스럽게 조립하세요.",
    context: "팀원과 상의하다 → 일정을 바꾸기로 결정하다",
    blocks: blocks("c17", ["일정을", "팀원과 상의한 뒤에", "바꾸기로 했어요"]),
    answer: ["c17-1", "c17-0", "c17-2"],
    correctSentence: "팀원과 상의한 뒤에 일정을 바꾸기로 했어요.",
    explanationKo: "상의가 먼저 끝났고, 그 결과로 일정 변경을 결정했어요.",
    explanationEn: "The sentence places the discussion first and uses ‘-기로 했어요’ for the resulting decision.",
    tag: "의도와 목적",
  },
  {
    id: "f1",
    stage: 3,
    type: "choice",
    prompt: "약속 시간에 늦지 않기 위해 지하철을 탔어요.",
    context: "가장 자연스러운 한 문장을 고르세요.",
    options: [
      "약속에 늦지 않으려고 지하철을 탔어요.",
      "약속에 늦지 않아서 지하철을 탔어요.",
      "약속에 늦지 않고 나서 지하철을 탔어요.",
    ],
    answer: "약속에 늦지 않으려고 지하철을 탔어요.",
    correctSentence: "약속에 늦지 않으려고 지하철을 탔어요.",
    explanationKo: "어떤 행동의 목적은 ‘-(으)려고’로 연결해요.",
    explanationEn: "Use ‘-(으)려고’ to express the purpose of an action.",
    tag: "의도와 목적",
  },
  {
    id: "f2",
    stage: 3,
    type: "choice",
    prompt: "자료를 모두 읽은 후 의견을 정리할 계획이에요.",
    context: "가장 자연스러운 한 문장을 고르세요.",
    options: [
      "자료를 다 읽은 다음에 의견을 정리하려고 해요.",
      "자료를 다 읽으려고 의견을 정리했어요.",
      "자료를 다 읽어서 다음에 의견을 정리해요.",
    ],
    answer: "자료를 다 읽은 다음에 의견을 정리하려고 해요.",
    correctSentence: "자료를 다 읽은 다음에 의견을 정리하려고 해요.",
    explanationKo: "행동 순서는 ‘다음에’, 앞으로의 계획은 ‘-(으)려고 해요’로 나타내요.",
    explanationEn: "Use ‘다음에’ for sequence and ‘-(으)려고 해요’ for a plan.",
    tag: "문장 마무리",
  },
  {
    id: "f3",
    stage: 3,
    type: "choice",
    prompt: "친구에게 연락할 생각이었지만 너무 늦어서 하지 못했어요.",
    context: "가장 자연스러운 한 문장을 고르세요.",
    options: [
      "친구에게 연락하려고 했는데 너무 늦었어요.",
      "친구에게 연락하고 나서 너무 늦으려고 했어요.",
      "친구에게 연락했기 때문에 너무 늦을 거예요.",
    ],
    answer: "친구에게 연락하려고 했는데 너무 늦었어요.",
    correctSentence: "친구에게 연락하려고 했는데 너무 늦었어요.",
    explanationKo: "하려던 일이 이루어지지 않았을 때 ‘-(으)려고 했는데’를 쓸 수 있어요.",
    explanationEn: "Use ‘-(으)려고 했는데’ for an intention that did not happen.",
    tag: "문장 마무리",
  },
  {
    id: "f4",
    stage: 3,
    type: "choice",
    prompt: "운동을 끝내고 샤워했어요.",
    context: "가장 자연스러운 한 문장을 고르세요.",
    options: [
      "운동을 마치고 나서 샤워했어요.",
      "운동을 마치려고 샤워했어요.",
      "운동을 마친 것 같아서 샤워할 거예요.",
    ],
    answer: "운동을 마치고 나서 샤워했어요.",
    correctSentence: "운동을 마치고 나서 샤워했어요.",
    explanationKo: "끝난 두 행동의 실제 순서는 ‘-고 나서’로 연결해요.",
    explanationEn: "Use ‘-고 나서’ for two completed actions in sequence.",
    tag: "순서 연결",
  },
  {
    id: "f5",
    stage: 3,
    type: "choice",
    prompt: "발표 전에 실수하지 않도록 여러 번 연습했어요.",
    context: "가장 자연스러운 한 문장을 고르세요.",
    options: [
      "발표할 때 실수하지 않으려고 여러 번 연습했어요.",
      "발표할 때 실수하지 않고 나서 여러 번 연습했어요.",
      "발표할 때 실수하지 않아서 여러 번 연습할게요.",
    ],
    answer: "발표할 때 실수하지 않으려고 여러 번 연습했어요.",
    correctSentence: "발표할 때 실수하지 않으려고 여러 번 연습했어요.",
    explanationKo: "연습한 목적이므로 ‘-지 않으려고’가 자연스러워요.",
    explanationEn: "‘-지 않으려고’ expresses the purpose of avoiding something.",
    tag: "의도와 목적",
  },
  {
    id: "f6",
    stage: 3,
    type: "choice",
    prompt: "일정을 확인한 후 비행기 표를 예약할 거예요.",
    context: "가장 자연스러운 한 문장을 고르세요.",
    options: [
      "일정을 확인한 뒤에 비행기 표를 예약할게요.",
      "일정을 확인하려고 비행기 표를 예약했어요.",
      "일정을 확인한 것 같아서 비행기 표를 예약했어요.",
    ],
    answer: "일정을 확인한 뒤에 비행기 표를 예약할게요.",
    correctSentence: "일정을 확인한 뒤에 비행기 표를 예약할게요.",
    explanationKo: "순서와 화자의 의지를 ‘-(으)ㄴ 뒤에 … -(으)ㄹ게요’로 나타냈어요.",
    explanationEn: "This pattern combines sequence with the speaker's intention.",
    tag: "문장 마무리",
  },
  {
    id: "f7",
    stage: 3,
    type: "choice",
    prompt: "결과가 확실하지 않지만 다음 주에 나올 것으로 예상해요.",
    context: "가장 자연스러운 한 문장을 고르세요.",
    options: [
      "결과는 아마 다음 주에 나올 것 같아요.",
      "결과의 대부분은 다음 주에 나올 것 같아요.",
      "결과는 보통 다음 주에 거의 나왔어요.",
    ],
    answer: "결과는 아마 다음 주에 나올 것 같아요.",
    correctSentence: "결과는 아마 다음 주에 나올 것 같아요.",
    explanationKo: "불확실한 예상은 ‘아마 … -(으)ㄹ 것 같아요’로 자연스럽게 말할 수 있어요.",
    explanationEn: "Use ‘아마 … -(으)ㄹ 것 같아요’ for an uncertain expectation.",
    tag: "부사 뉘앙스",
  },
  {
    id: "f8",
    stage: 3,
    type: "choice",
    prompt: "회의가 끝난 후 결정한 내용을 이메일로 보냈어요.",
    context: "가장 자연스러운 한 문장을 고르세요.",
    options: [
      "회의가 끝난 다음에 결정한 내용을 이메일로 보냈어요.",
      "회의가 끝나려고 결정한 내용을 이메일로 보냈어요.",
      "회의가 끝날 것 같아서 결정한 내용을 보내려고 했어요.",
    ],
    answer: "회의가 끝난 다음에 결정한 내용을 이메일로 보냈어요.",
    correctSentence: "회의가 끝난 다음에 결정한 내용을 이메일로 보냈어요.",
    explanationKo: "완료된 사건의 순서를 ‘-(으)ㄴ 다음에’로 분명하게 나타냈어요.",
    explanationEn: "‘-(으)ㄴ 다음에’ clearly shows the order of completed events.",
    tag: "순서 연결",
  },
  {
    id: "f9",
    stage: 3,
    type: "choice",
    prompt: "새 휴대폰을 사기 전에 여러 제품을 비교할 생각이에요.",
    context: "가장 자연스러운 한 문장을 고르세요.",
    options: [
      "여러 제품을 비교해 보고 나서 새 휴대폰을 사려고 해요.",
      "여러 제품을 비교하려고 새 휴대폰을 샀어요.",
      "여러 제품을 비교한 것 같아서 새 휴대폰을 사요.",
    ],
    answer: "여러 제품을 비교해 보고 나서 새 휴대폰을 사려고 해요.",
    correctSentence: "여러 제품을 비교해 보고 나서 새 휴대폰을 사려고 해요.",
    explanationKo: "비교하는 과정이 먼저이고 구매 계획이 그 뒤에 이어져요.",
    explanationEn: "The comparison comes first, followed by the purchase plan.",
    tag: "의도와 목적",
  },
  {
    id: "f10",
    stage: 3,
    type: "choice",
    prompt: "집에 도착하면 바로 부모님께 연락하는 것이 제 습관이에요.",
    context: "가장 자연스러운 한 문장을 고르세요.",
    options: [
      "집에 도착하면 바로 부모님께 연락해요.",
      "집에 도착하고 나서 바로 연락하려고 했어요.",
      "집에 도착할 것 같아서 부모님께 연락했어요.",
    ],
    answer: "집에 도착하면 바로 부모님께 연락해요.",
    correctSentence: "집에 도착하면 바로 부모님께 연락해요.",
    explanationKo: "반복되는 조건과 습관에는 ‘-(으)면’이 간결하고 자연스러워요.",
    explanationEn: "‘-(으)면’ is natural for a repeated condition and habit.",
    tag: "문장 마무리",
  },
  {
    id: "f11",
    stage: 3,
    type: "choice",
    prompt: "고객의 요청을 확인한 후 답변을 보냈어요.",
    context: "가장 자연스러운 한 문장을 고르세요.",
    options: [
      "고객의 요청을 확인한 뒤에 답변을 보냈어요.",
      "고객의 요청을 확인하려고 답변을 보냈어요.",
      "고객의 요청을 확인할 것 같아서 답변을 보내요.",
    ],
    answer: "고객의 요청을 확인한 뒤에 답변을 보냈어요.",
    correctSentence: "고객의 요청을 확인한 뒤에 답변을 보냈어요.",
    explanationKo: "완료된 두 업무의 순서를 ‘-(으)ㄴ 뒤에’로 분명하게 연결해요.",
    explanationEn: "Use ‘-(으)ㄴ 뒤에’ to show that checking the request was completed before sending the reply.",
    tag: "순서 연결",
  },
  {
    id: "f12",
    stage: 3,
    type: "choice",
    prompt: "건강을 위해 매일 아침 일찍 일어나 운동해요.",
    context: "가장 자연스러운 한 문장을 고르세요.",
    options: [
      "아침에 운동하려고 일찍 일어나요.",
      "아침에 운동하고 나서 일찍 일어나요.",
      "아침에 운동해서 일찍 일어나려고 했어요.",
    ],
    answer: "아침에 운동하려고 일찍 일어나요.",
    correctSentence: "아침에 운동하려고 일찍 일어나요.",
    explanationKo: "일찍 일어나는 목적이 운동이므로 ‘-(으)려고’를 사용해요.",
    explanationEn: "‘-(으)려고’ marks exercising as the purpose of getting up early.",
    tag: "의도와 목적",
  },
  {
    id: "f13",
    stage: 3,
    type: "choice",
    prompt: "회의가 시작될 때마다 휴대폰을 무음으로 바꾸는 습관이 있어요.",
    context: "가장 자연스러운 한 문장을 고르세요.",
    options: [
      "회의가 시작되면 휴대폰을 무음으로 바꿔요.",
      "회의가 시작하고 나서 휴대폰을 바꾸려고 했어요.",
      "회의가 시작될 것 같아서 휴대폰을 바꿨어요.",
    ],
    answer: "회의가 시작되면 휴대폰을 무음으로 바꿔요.",
    correctSentence: "회의가 시작되면 휴대폰을 무음으로 바꿔요.",
    explanationKo: "반복되는 조건과 습관적 행동은 ‘-(으)면’으로 연결해요.",
    explanationEn: "Use ‘-(으)면’ because the same action regularly follows whenever the meeting starts.",
    tag: "문장 마무리",
  },
  {
    id: "f14",
    stage: 3,
    type: "choice",
    prompt: "교통 상황을 보면 도착 시간이 조금 늦어질 것으로 예상해요.",
    context: "가장 자연스러운 한 문장을 고르세요.",
    options: [
      "교통이 막혀서 아마 조금 늦게 도착할 것 같아요.",
      "교통의 대부분이 막혀서 약 늦게 도착해요.",
      "교통이 보통 막히고 나서 늦게 도착했어요.",
    ],
    answer: "교통이 막혀서 아마 조금 늦게 도착할 것 같아요.",
    correctSentence: "교통이 막혀서 아마 조금 늦게 도착할 것 같아요.",
    explanationKo: "현재 상황을 근거로 한 불확실한 예상은 ‘아마 … 것 같아요’로 말해요.",
    explanationEn: "‘아마 … 것 같아요’ naturally presents a prediction that is possible but not certain.",
    tag: "부사 뉘앙스",
  },
  {
    id: "f15",
    stage: 3,
    type: "choice",
    prompt: "팀 회의에서 다음 달부터 새 도구를 사용하기로 결정했어요.",
    context: "가장 자연스러운 한 문장을 고르세요.",
    options: [
      "다음 달부터 새 도구를 사용하기로 했어요.",
      "다음 달부터 새 도구를 사용하려고 했는데요.",
      "다음 달부터 새 도구를 사용하고 나서 결정해요.",
    ],
    answer: "다음 달부터 새 도구를 사용하기로 했어요.",
    correctSentence: "다음 달부터 새 도구를 사용하기로 했어요.",
    explanationKo: "회의에서 이미 확정한 결정이므로 ‘-기로 했어요’를 사용해요.",
    explanationEn: "Use ‘-기로 했어요’ because the team has already made and settled the decision.",
    tag: "의도와 목적",
  },
  {
    id: "f16",
    stage: 3,
    type: "choice",
    prompt: "은행에 가려고 했지만 업무가 늦게 끝나서 가지 못했어요.",
    context: "가장 자연스러운 한 문장을 고르세요.",
    options: [
      "은행에 가려고 했는데 업무가 늦게 끝났어요.",
      "은행에 가고 나서 업무가 늦게 끝나려고 했어요.",
      "은행에 갔기 때문에 업무가 늦게 끝날 것 같아요.",
    ],
    answer: "은행에 가려고 했는데 업무가 늦게 끝났어요.",
    correctSentence: "은행에 가려고 했는데 업무가 늦게 끝났어요.",
    explanationKo: "실행하지 못한 의도와 방해 상황은 ‘-(으)려고 했는데’로 연결해요.",
    explanationEn: "‘-(으)려고 했는데’ contrasts the intended trip with the work situation that prevented it.",
    tag: "문장 마무리",
  },
];

export const stageMeta = {
  1: { label: "Choose the Nuance", short: "Compare similar meanings", tone: "violet" },
  2: { label: "Connect Sentence Blocks", short: "Practice natural word order", tone: "mint" },
  3: { label: "Finish Naturally", short: "Complete the sentence", tone: "amber" },
} as const;
